import React from "react";
import Immutable from "immutable";
import moment from "moment";
import {RRule} from "rrule";
import {FormControl, FormControlLabel, Radio, RadioGroup} from "@mui/material";

import Select from "js/common/views/inputs/immutable-react-select";
import SimpleTextInput from "js/common/views/inputs/simple-text-input";
import DatePicker from "js/common/views/inputs/timeframe-picker/react-datepicker";
import LoadingSpinner from "js/common/views/loading-spinner";
import FormField from "js/common/views/inputs/form-field";
import * as Ajax from "js/common/ajax";
import {
  frequencyOptions,
  getConfigFromSchedule,
  getMonthlyPatternInfo,
  getScheduleFromConfig,
  monthOptions,
  weekdayOptions,
  getDefaultRuleConfig
} from "js/dashboards/scheduling/utils";
import ScheduleSummary from "js/dashboards/scheduling/schedule-summary";

// Generate time slot options for the dropdown
const generateTimeSlotOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    const startHour = String(hour).padStart(2, "0");
    const endHour = String((hour + 1) % 24).padStart(2, "0");
    const startTime = moment(`${startHour}:00`, "HH:mm");
    const endTime = moment(`${endHour}:00`, "HH:mm");
    const startFormatted = startTime.format("h A");
    const endFormatted = endTime.format("h A");

    options.push(
      Immutable.Map({
        value: startHour + ":00:00",
        label: `${startFormatted} - ${endFormatted}`
      })
    );
  }
  return Immutable.List(options);
};

const RruleScheduler = ({schedule, onChange, errors, onErrorsChange}) => {
  const optionHeadingStyle = {
    fontSize: 14,
    margin: 0,
    marginTop: 20,
    padding: 0
  };

  const [timezoneOptions, setTimezoneOptions] = React.useState(Immutable.List());
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    Ajax
      .get({url: window.path("/locations")})
      .then((response) => {
        const options = Immutable.fromJS(response).map((timezone) =>
          Immutable.Map({
            value: timezone,
            label: timezone
          })
        );
        setTimezoneOptions(options);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load timezone options:", error);
        setIsLoading(false);
      });
  }, []);

  const [config, setConfig] = React.useState(() => {
    if (schedule) {
      try {
        return getConfigFromSchedule(schedule);
      } catch (e) {
        console.error("Error parsing rule string:", e);
      }
    }
    return getDefaultRuleConfig();
  });

  const startDate = config.get("startDate");
  const isValidStartDate = React.useMemo(() => {
    return moment(startDate).isValid();
  }, [startDate]);

  const monthlyPatternInfo = React.useMemo(() => {
    return getMonthlyPatternInfo(startDate);
  }, [startDate]);

  const updateConfig = React.useCallback(
    (key, val) => {
      setConfig((prev) => {
        const newConfig = prev.set(key, val);

        const newStartDate = newConfig.get("startDate");
        const newMonthlyPatternInfo = getMonthlyPatternInfo(newStartDate);
        const schedule = getScheduleFromConfig(newConfig, newMonthlyPatternInfo);
        if (schedule) {
          onChange(schedule);
        }

        return newConfig;
      });
    },
    [onChange]
  );

  return (
    <div style={{display: "flex", justifyContent: "space-between"}}>
      <div style={{width: "49%"}}>
        <div style={{marginBottom: "20px"}}>
          <FormField label="Start Date" errorMessage={errors.get("startDate")}>
            <DatePicker
              value={moment(startDate)}
              onDateChange={(value) => updateConfig("startDate", value.format("YYYY-MM-DD"))}
              onErrorChange={(e) => onErrorsChange("startDate", e)}
              height={42}
              hideErrorMessage
              calendarPosition="left"
            />
          </FormField>
        </div>

        <div style={{display: "flex", gap: "10px", marginBottom: "20px"}}>
          <div style={{flex: 1}}>
            <FormField
              label="Send Time"
              tooltip="During periods of high processing volume, reports may be sent within a one-hour window of the scheduled time."
              errorMessage={errors.get("startTime")}
            >
              <Select
                selectedValue={config.get("startTime")}
                onChange={(startTime) => updateConfig("startTime", startTime)}
                options={generateTimeSlotOptions()}
                isClearable={false}
                searchable={false}
                isMulti={false}
                placeholder="Select time..."
              />
            </FormField>
          </div>
          <div style={{flex: 1, position: "relative", top: "-2px"}}>
            <label style={{fontSize: "0.8rem", padding: "0 5px 0 0", position: "relative"}}>Timezone</label>
            {!isLoading ? (
              <Select
                selectedValue={config.get("timezone")}
                onChange={(timezone) => updateConfig("timezone", timezone)}
                options={timezoneOptions}
                isClearable={false}
                searchable={true}
                isMulti={false}
              />
            ) : (
              <LoadingSpinner />
            )}
          </div>
        </div>

        <div style={{marginBottom: "20px"}}>
          <FormField label="Repeat Every">
            <div style={{display: "flex", gap: "10px", width: "100%"}}>
              <SimpleTextInput
                type="number"
                value={config.get("interval")}
                onChange={(value) => updateConfig("interval", parseInt(value) || 1)}
                InputProps={{inputProps: {min: 1}}}
                size="small"
                customStyle={{flex: 1, height: 42}}
              />
              <div style={{flex: 1, minWidth: 150}}>
                <Select
                  selectedValue={config.get("freq")}
                  onChange={(freqValue) => updateConfig("freq", freqValue)}
                  options={frequencyOptions}
                  isClearable={false}
                  searchable={false}
                  isMulti={false}
                  style={{flex: 1, height: 36}}
                />
              </div>
            </div>
          </FormField>
        </div>

        {config.get("freq") === RRule.WEEKLY && (
          <div style={{marginBottom: "20px"}}>
            <Select
              selectedValues={config.get("byweekday")}
              onChange={(weekdayValues) => updateConfig("byweekday", weekdayValues)}
              options={weekdayOptions}
              isMulti={true}
              isClearable={false}
            />
          </div>
        )}

        {config.get("freq") === RRule.MONTHLY && (
          <div style={{marginBottom: "20px"}}>
            <Select
              selectedValue={config.get("monthlyPattern")}
              onChange={(pattern) => updateConfig("monthlyPattern", pattern)}
              options={Immutable.List([
                Immutable.Map({
                  value: "dayOfMonth",
                  label: isValidStartDate ? `Day ${monthlyPatternInfo.dayOfMonth} of every month` : "Day of the month"
                }),
                Immutable.Map({
                  value: "dayOfWeek",
                  label: isValidStartDate
                    ? `${monthlyPatternInfo.ordinal} ${monthlyPatternInfo.dayName} of every month`
                    : "Day of the week"
                })
              ])}
              isClearable={false}
              searchable={false}
              isMulti={false}
            />
          </div>
        )}

        {config.get("freq") === RRule.YEARLY && (
          <div style={{marginBottom: "20px"}}>
            <Select
              selectedValues={config.get("bymonth")}
              onChange={(monthValues) => updateConfig("bymonth", monthValues)}
              options={monthOptions}
              isMulti={true}
              isClearable={false}
            />
          </div>
        )}
      </div>

      <div style={{width: "49%"}}>
        <FormControl component="fieldset" style={{marginBottom: "20px"}}>
          <p style={optionHeadingStyle}>When would you like this schedule to end?</p>
          <RadioGroup
            style={{gap: 8}}
            value={config.get("endMode")}
            onChange={(e) => updateConfig("endMode", e.target.value)}
          >
            <FormControlLabel value="never" control={<Radio size="small" />} label="Never" />
            <div
              style={{display: "flex", gap: 0, width: "100%"}}
              onClick={(e) => {
                e.stopPropagation();
                updateConfig("endMode", "until");
              }}
            >
              <FormControlLabel value="until" control={<Radio size="small" />} label="End on" />
              <DatePicker
                value={config.get("until") ? moment(config.get("until")) : moment(startDate).add(1, "day")}
                minDate={moment(startDate).add(1, "day")}
                hideErrorMessage={config.get("endMode") !== "until"}
                hideError={config.get("endMode") !== "until"}
                onDateChange={(value) => {
                  updateConfig("endMode", "until");
                  updateConfig("until", value.format("YYYY-MM-DD"));
                }}
                onErrorChange={(e) => onErrorsChange("endDate", e)}
                calendarPosition="left"
              />
            </div>
            <FormControlLabel
              value="count"
              control={<Radio size="small" />}
              label={
                <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                  <span>End after</span>
                  <SimpleTextInput
                    type="number"
                    value={config.get("count") || 1}
                    onChange={(value) => {
                      updateConfig("endMode", "count");
                      updateConfig("count", Math.max(1, parseInt(value) || 1));
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      updateConfig("endMode", "count");
                    }}
                    InputProps={{inputProps: {min: 1}}}
                    size="small"
                    style={{width: "80px"}}
                    placeholder="Occurrences"
                  />
                  <span>occurrences</span>
                </div>
              }
            />
          </RadioGroup>
        </FormControl>
        <ScheduleSummary
          config={config}
          rrule={schedule.get("rrule")}
          timezoneOptions={timezoneOptions}
          frequencyOptions={frequencyOptions}
          monthOptions={monthOptions}
          monthlyPatternInfo={monthlyPatternInfo}
          style={{marginTop: 6}}
        />
      </div>
    </div>
  );
};

export default RruleScheduler;
